/**
 * Local persistence for the native onboarding quiz. Answers are written as
 * they're given so a killed app resumes at the same step, and they survive
 * through sign-up so the paywall can personalize from them. Everything here
 * is per-install by design (pre-auth there is no server identity); the
 * durable cross-device record is `users.is_tour_completed`, set after auth.
 */

const ANSWERS_KEY = "insight:onboarding-answers";
const SIGNUP_PENDING_KEY = "insight:onboarding-signup-pending";

export interface OnboardingAnswers {
  experience?: string;
  goal?: string;
  decide?: string[];
  struggle?: string;
  time?: string;
  markets?: string[];
}

interface OnboardingState {
  step: number;
  answers: OnboardingAnswers;
  completedAt?: string;
}

const EMPTY_STATE: OnboardingState = { step: 0, answers: {} };

export const getOnboardingState = (): OnboardingState => {
  try {
    const raw = localStorage.getItem(ANSWERS_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as OnboardingState;
    if (typeof parsed.step !== "number" || typeof parsed.answers !== "object") {
      return EMPTY_STATE;
    }
    return parsed;
  } catch {
    return EMPTY_STATE;
  }
};

export const saveOnboardingProgress = (
  step: number,
  answers: OnboardingAnswers,
): void => {
  try {
    const completedAt = getOnboardingState().completedAt;
    localStorage.setItem(
      ANSWERS_KEY,
      JSON.stringify({ step, answers, completedAt }),
    );
  } catch {
    // Storage unavailable — the quiz just restarts next launch
  }
};

export const markOnboardingCompleted = (): void => {
  try {
    const state = getOnboardingState();
    localStorage.setItem(
      ANSWERS_KEY,
      JSON.stringify({ ...state, completedAt: new Date().toISOString() }),
    );
    localStorage.setItem(SIGNUP_PENDING_KEY, "true");
  } catch {
    // Storage unavailable — completion just isn't remembered
  }
};

/** Answers of a COMPLETED quiz, for paywall personalization. Null until done. */
export const getCompletedOnboardingAnswers = (): OnboardingAnswers | null => {
  const state = getOnboardingState();
  if (!state.completedAt) return null;
  return state.answers;
};

/**
 * True between quiz completion and the first authenticated visit — the window
 * where a fresh launch should land on sign-up (their plan is waiting) rather
 * than sign-in.
 */
export const isOnboardingSignupPending = (): boolean => {
  try {
    return localStorage.getItem(SIGNUP_PENDING_KEY) === "true";
  } catch {
    return false;
  }
};

export const clearOnboardingSignupPending = (): void => {
  try {
    localStorage.removeItem(SIGNUP_PENDING_KEY);
  } catch {
    // Nothing to clear
  }
};
